import type { ApexOptions } from 'apexcharts'
import type { Theme } from '@/shared/theme/ThemeContext'

/**
 * ApexCharts takes colours as option strings, so the palette is read out of the
 * `--ln-*` custom properties in `styles/globals.css`, which are its single
 * source.
 */
export type ChartPalette = {
  positive: string
  negative: string
  /** Series colours, in order (chart-01, 03, 05, 02, 04, 06, 07). */
  series: string[]
  grid: string
  label: string
  text: string
  card: string
  crosshair: string
  /** The expanded card's neutral sand bars, dashed grid and x-axis line. */
  bar: string
  barGrid: string
  baseline: string
  /** Axis labels and tooltip date on the expanded card's chart. */
  textAlt: string
  /** The global target line and its label. */
  goal: string
}

// Where no stylesheet is loaded (jsdom), so there are no tokens to read.
const FALLBACK_COLOR = '#808080'

const SERIES_COUNT = 7

const cache = new Map<Theme, ChartPalette>()

/**
 * The palette for a theme. It is read from a probe element carrying that
 * theme's `data-theme`, not from `<html>`, which flips a render after
 * ThemeContext does.
 */
export const getChartPalette = (theme: Theme): ChartPalette => {
  const cached = cache.get(theme)
  if (cached) return cached
  const probe = document.createElement('div')
  probe.dataset['theme'] = theme
  probe.hidden = true
  document.body.appendChild(probe)
  const style = getComputedStyle(probe)
  const token = (name: string) =>
    style.getPropertyValue(`--ln-${name}`).trim() || FALLBACK_COLOR
  const palette: ChartPalette = {
    positive: token('positive'),
    negative: token('negative'),
    series: Array.from({ length: SERIES_COUNT }, (_, i) =>
      token(`chart-series-${i + 1}`)
    ),
    grid: token('chart-grid'),
    label: token('text-secondary'),
    text: token('text'),
    card: token('surface'),
    crosshair: token('crosshair'),
    bar: token('chart-bar'),
    barGrid: token('chart-bar-grid'),
    baseline: token('chart-baseline'),
    textAlt: token('text-alt'),
    goal: token('chart-goal'),
  }
  probe.remove()
  cache.set(theme, palette)
  return palette
}

const chartFontFamily =
  "'N26', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

export const baseOptions = (palette: ChartPalette): ApexOptions => ({
  chart: {
    background: 'transparent',
    toolbar: { show: false },
    fontFamily: chartFontFamily,
    foreColor: palette.label,
  },
  dataLabels: { enabled: false },
  legend: { labels: { colors: palette.label } },
})
